import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ADDRESS, EMAIL, HOURS, MAP_EMBED, MAP_LINK, PHONE, PHONE_TEL, SERVICES, WHATSAPP_LINK } from "@/data/site";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ tone = "rose", defaultService = "Hair Cut" }) => {
  const [form, setForm] = useState({ name: "", phone: "", service: defaultService });
  const [submitting, setSubmitting] = useState(false);
  const emerald = tone === "emerald";
  const accent = emerald ? "text-[#D48C46]" : "text-rose-400";
  const primary = emerald ? "bg-[#D48C46] hover:bg-[#E5A862] text-slate-950" : "bg-rose-600 hover:bg-rose-500 text-white";
  const focus = emerald ? "focus:border-[#D48C46]" : "focus:border-rose-500";
  const surface = emerald ? "bg-[#06140E] border-emerald-900/50" : "bg-slate-900 border-slate-800";

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request sent! We will call you back to confirm.", { description: `Service: ${form.service}` });
      setForm({ name: "", phone: "", service: defaultService });
    } catch {
      toast.error("Could not send your request. Please call us directly.", { description: PHONE });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className={`py-24 border-t ${emerald ? "bg-[#030B08] border-emerald-900/40" : "bg-slate-950 border-slate-800"}`} data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-6">
            <div>
              <span className={`text-xs font-semibold uppercase tracking-widest ${accent}`}>{emerald ? "Orders & Enquiries" : "Quick Appointment"}</span>
              <h2 className="font-condensed text-3xl sm:text-4xl uppercase text-white mt-2">Get in Touch</h2>
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">Call our reception for direct assistance or send a request below — we reply on call or WhatsApp.</p>
            </div>

            <div className={`p-6 rounded-2xl border ${surface}`}>
              <span className="text-xs text-slate-400 font-semibold uppercase">Telephone Booking</span>
              <div className={`text-2xl font-bold mt-1 ${accent}`} data-testid="contact-phone-number">{PHONE}</div>
              <a href={PHONE_TEL} data-testid="contact-call-btn" className={`mt-4 inline-flex items-center justify-center w-full py-3 text-xs uppercase font-bold tracking-wider rounded-xl transition-colors ${primary}`}>
                Call {emerald ? "Zoryven" : "Fancy Saloon"}
              </a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="mt-3 inline-flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] hover:bg-[#1ebe5b] text-slate-950 text-xs uppercase font-bold tracking-wider rounded-xl transition-colors">
                <WhatsAppIcon /> WhatsApp Us
              </a>
            </div>

            <div className={`p-4 rounded-xl border text-xs text-slate-400 space-y-2 ${emerald ? "bg-[#06140E]/60 border-emerald-900/40" : "bg-slate-900/40 border-slate-800/80"}`}>
              <p><span className="font-bold text-slate-300">Email:</span> <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors" data-testid="contact-email-link">{EMAIL}</a></p>
              <p><span className="font-bold text-slate-300">Address:</span> <a href={MAP_LINK} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="contact-address">{ADDRESS}</a></p>
              <p><span className="font-bold text-slate-300">Hours:</span> <span data-testid="contact-hours">{HOURS}</span></p>
            </div>

            <div className={`rounded-2xl overflow-hidden border ${surface}`} data-testid="contact-map">
              <iframe title="Fancy Saloon location map" src={MAP_EMBED} width="100%" height="220" style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
              <a href={MAP_LINK} target="_blank" rel="noreferrer" data-testid="contact-directions-link" className={`block text-center py-3 text-xs uppercase font-bold tracking-wider transition-colors ${accent} hover:text-white`}>
                Get Directions
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className={`p-8 rounded-3xl border ${emerald ? "bg-[#06140E]/80 border-emerald-900/50" : "bg-slate-900/80 border-slate-800"}`}>
              <h3 className="text-lg font-bold text-white mb-2">{emerald ? "Order Products or Book a Treatment" : "Book Service or Order Care Products"}</h3>
              <p className="text-xs text-slate-400 mb-6">Choose what you need and we will call you back.</p>
              <form className="space-y-4" onSubmit={handleSubmit} data-testid="booking-form">
                <div>
                  <label htmlFor="booking-name" className="block text-xs uppercase font-semibold text-slate-300 mb-1">Your Full Name</label>
                  <input id="booking-name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="Name" data-testid="booking-name-input" className={`w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none ${focus}`} />
                </div>
                <div>
                  <label htmlFor="booking-phone" className="block text-xs uppercase font-semibold text-slate-300 mb-1">Phone Number</label>
                  <input id="booking-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Mobile Number" data-testid="booking-phone-input" className={`w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none ${focus}`} />
                </div>
                <div>
                  <label htmlFor="booking-service" className="block text-xs uppercase font-semibold text-slate-300 mb-1">Service / Product</label>
                  <select id="booking-service" name="service" value={form.service} onChange={handleChange} data-testid="booking-service-select" className={`w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-sm focus:outline-none ${focus}`}>
                    <option value="Product Inquiry">Product Inquiry (Hair Oil, Shampoo, Beard Oil, Powder, Serum)</option>
                    {SERVICES.map((s) => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" disabled={submitting} data-testid="booking-submit-btn" className={`w-full py-4 disabled:opacity-60 font-bold uppercase tracking-wider text-xs rounded-xl transition-colors ${primary}`}>
                  {submitting ? "Sending..." : "Submit Request"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
